import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { CaretDown, Check } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: React.ReactNode
  /** Optional secondary line rendered under the label in the menu — muted and
   *  smaller (e.g. an email under a person's name). */
  description?: string
}

export interface SelectGroup {
  label: string
  options: SelectOption[]
}

export interface SelectProps {
  value: string
  onChange: (value: string) => void
  /** Flat option list. Provide this OR `groups`, not both. */
  options?: SelectOption[]
  /** Grouped options — renders non-interactive category headers above each set. */
  groups?: SelectGroup[]
  placeholder?: string
  className?: string
  buttonClassName?: string
  ariaLabel?: string
  /**
   * Id applied to the trigger button, so an external `<label htmlFor>` can name
   * it. Prefer this over `ariaLabel` when a visible label already exists — see
   * `ui/Field`, which supplies it automatically (§56).
   */
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  disabled?: boolean
  /** Optional leading icon rendered inside the trigger, before the label. */
  icon?: React.ReactNode
  /**
   * Minimum width of the trigger button. The menu defaults to the trigger
   * width but expands to fit the widest label.
   */
  minWidth?: number | string
  /** Max height of the dropdown menu in px. Defaults to 288 (max-h-72). */
  menuMaxHeight?: number
}

/** How long a typeahead prefix survives between keystrokes (ARIA APG guidance). */
const TYPEAHEAD_RESET_MS = 500

/**
 * Floor for the menu's computed max height. When a trigger sits flush against a
 * viewport edge the room on both sides collapses to ~0, and honouring that
 * literally rendered a zero-height sliver. Better to overlap the trigger than to
 * be invisible — `computeMenuStyle` clamps the position so the floored menu is
 * still fully on screen.
 */
const MIN_MENU_HEIGHT = 160

/**
 * Typeahead can only match options whose label is a plain string. `label` is a
 * `React.ReactNode`, so an element / array / number label is skipped rather than
 * stringified — no crash, no nonsense match on "[object Object]".
 */
const typeaheadLabel = (opt: SelectOption): string | null =>
  typeof opt.label === 'string' ? opt.label.trim().toLowerCase() : null

/**
 * Editorial Dark custom select. Replaces native `<select>` so the dropdown
 * looks identical across macOS / Windows / Linux and respects light + dark
 * theming.
 *
 * Menu is portal-rendered to `document.body` with fixed positioning so it
 * escapes any `overflow-hidden` / `overflow-auto` ancestor (e.g. a filter
 * popover, a Card with rounded corners, or a scroll container).
 */
const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      value,
      onChange,
      options,
      groups,
      placeholder = 'Select...',
      className,
      buttonClassName,
      ariaLabel,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      disabled = false,
      icon,
      minWidth,
      menuMaxHeight = 288,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState<number>(-1)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null)
    const triggerRef = useRef<HTMLButtonElement | null>(null)
    const menuRef = useRef<HTMLUListElement | null>(null)

    // Stable id base so the trigger can point `aria-activedescendant` at the
    // highlighted option — that is what makes arrow-key nav announce.
    const uid = React.useId()
    const listboxId = `${uid}listbox`
    const optionId = useCallback((flatIndex: number) => `${uid}opt-${flatIndex}`, [uid])

    // Flattened option list (group-aware) — single source of truth for the
    // selected label, keyboard nav, and the active-index → option mapping.
    //
    // MUST stay memoized: `groups.flatMap(...)` allocates a fresh array on every
    // render, and this list feeds effect dependency arrays below. An unstable
    // identity here re-fires those effects on every render, which is what used
    // to stomp `activeIndex` back to the selected option mid-arrow-key-nav.
    const flatOptions: SelectOption[] = useMemo(
      () => (groups ? groups.flatMap((g) => g.options) : options ?? []),
      [groups, options],
    )

    // Compose external ref with internal triggerRef
    const setTriggerRef = useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
      },
      [ref],
    )

    // Index of the selected option in `flatOptions`, or -1 when nothing matches.
    //
    // DELIBERATE: selection is matched by `value` — that is the component's
    // contract (`value: string`), and 66 call sites depend on it. So when the same
    // `value` is offered under two groups, the FIRST occurrence is "the" selected
    // row: it owns the check mark, the trigger label, and the seeded highlight.
    // Everything else about an option (React key, DOM id, highlight target, hover)
    // is keyed on its `flatIndex`, so duplicate values can no longer collide or
    // mis-target a different row than the one the user is pointing at.
    const selectedIndex = useMemo(
      () => flatOptions.findIndex((o) => o.value === value),
      [flatOptions, value],
    )
    const selected = selectedIndex >= 0 ? flatOptions[selectedIndex] : undefined

    // Printable-character typeahead buffer. A ref, not state: it must survive the
    // re-render that moving the highlight causes, and nothing renders from it.
    const typeaheadRef = useRef<{ buffer: string; timer: number | null }>({
      buffer: '',
      timer: null,
    })
    const resetTypeahead = useCallback(() => {
      if (typeaheadRef.current.timer !== null) window.clearTimeout(typeaheadRef.current.timer)
      typeaheadRef.current = { buffer: '', timer: null }
    }, [])
    // A pending buffer-clear timer must not outlive the component.
    useEffect(() => resetTypeahead, [resetTypeahead])

    const close = useCallback(() => {
      setOpen(false)
      setActiveIndex(-1)
      resetTypeahead()
    }, [resetTypeahead])

    // Opening is the ONLY moment the active index is seeded. Doing it here
    // rather than in an effect keyed on `[open, flatOptions, value]` is what
    // keeps arrow-key nav alive: an effect like that re-ran on every render
    // while the menu was open and reset `activeIndex` (and therefore
    // `aria-activedescendant`) to the current selection after each keystroke.
    //
    // `seed` picks the fallback when nothing is selected yet — ArrowDown on a
    // closed trigger lands on the first option, ArrowUp on the last, and a plain
    // mouse click ('selected') also lands on the first rather than on nothing.
    //
    // That last case matters: leaving `activeIndex` at -1 while open (open by
    // mouse, nothing selected) meant Enter matched no branch in the while-open
    // listener and fell through to native <button> activation → onClick → close(),
    // i.e. Enter silently dismissed instead of committing. So for a NON-EMPTY list
    // `activeIndex` is now always a real index while open. -1 survives only for a
    // genuinely empty list, where there is nothing to point at.
    const openMenu = useCallback(
      (seed: 'selected' | 'first' | 'last' = 'selected') => {
        if (disabled) return
        const lastIndex = flatOptions.length - 1
        const fallback = seed === 'last' ? lastIndex : 0
        setActiveIndex(Math.min(selectedIndex >= 0 ? selectedIndex : fallback, lastIndex))
        setOpen(true)
      },
      [disabled, flatOptions.length, selectedIndex],
    )

    const computeMenuStyle = useCallback((): React.CSSProperties | null => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return null
      const margin = 6
      const viewportH = window.innerHeight
      const viewportW = window.innerWidth
      const maxMenuH = menuMaxHeight
      const spaceBelow = viewportH - rect.bottom - margin
      const spaceAbove = rect.top - margin
      // Open upward only when below is cramped AND above has more room — i.e.
      // always pick the roomier side once below is too tight to be usable.
      const openUp = spaceBelow < Math.min(maxMenuH, 200) && spaceAbove > spaceBelow
      // Clamp the menu to the room on the chosen side so it never runs off the
      // top or bottom of the viewport (the cause of the dropdown disappearing
      // below the window when the trigger sits low, e.g. in a tall modal)...
      const room = Math.max(0, openUp ? spaceAbove : spaceBelow)
      // ...but never below MIN_MENU_HEIGHT: a trigger flush against a viewport
      // edge leaves ~0px on BOTH sides, and honouring that rendered the menu as a
      // zero-height sliver. The floor is itself capped to the viewport so a very
      // short window can't be handed a max height taller than it is.
      const viewportRoom = Math.max(0, viewportH - margin * 2)
      const maxHeight = Math.min(maxMenuH, viewportRoom, Math.max(room, MIN_MENU_HEIGHT))
      // Keep the menu inside the viewport horizontally.
      const left = Math.max(margin, Math.min(rect.left, viewportW - rect.width - margin))
      const base: React.CSSProperties = {
        position: 'fixed',
        left,
        // The trigger width is a FLOOR, not the width. It used to be `width:
        // rect.width`, which pinned the menu to the trigger and truncated
        // every option in a narrow control -- the Content focus dropdown on
        // the deck form showed "Pitch D...", "Product Ov...", "Industry
        // An..." and cut every description (AB#22323). That also contradicted
        // this prop's own documentation, which has always said the menu
        // "expands to fit the widest label".
        minWidth: rect.width,
        // Growth runs rightward from the trigger's left edge, so clamp it to
        // what is actually on screen rather than letting a long label push
        // the panel off the viewport.
        maxWidth: Math.max(rect.width, viewportW - left - margin),
        maxHeight,
        zIndex: 1000,
      }
      // Anchor to the trigger, then clamp vertically against `maxHeight` so the
      // whole panel stays on screen. When the floor above wins, the clamp is what
      // makes it visible: the menu slides over its own trigger instead of
      // extending past the viewport edge. Overlapping the trigger is strictly
      // better than being off-screen.
      //
      // Opening up anchors the menu's BOTTOM edge to the trigger's top so a menu
      // shorter than maxHeight hugs the trigger instead of floating detached
      // above it; `bottom != null` is also how the caller below picks the
      // animation direction and transform origin.
      if (openUp) {
        base.bottom = Math.max(
          margin,
          Math.min(viewportH - rect.top + margin, viewportH - margin - maxHeight),
        )
      } else {
        base.top = Math.max(margin, Math.min(rect.bottom + margin, viewportH - margin - maxHeight))
      }
      return base
    }, [menuMaxHeight])

    // Position the menu once when it opens, then DISMISS on scroll or resize
    // (§54 R2). The menu is fixed to the viewport and never chases its trigger,
    // so letting the page scroll under it would leave a menu anchored to
    // nothing — §19 wins, and this matches DateTimePicker, the kit's reference
    // popover. Otherwise it closes on outside-click / Escape / selection.
    //
    // The scroll listener is capture-phase because `scroll` does not bubble —
    // that is what catches scrolling inside nested ancestor scrollers. The
    // menuRef guard is required: without it, scrolling the menu's own
    // `overflow-y-auto` list would close it on the first wheel tick.
    useLayoutEffect(() => {
      if (!open) {
        setMenuStyle(null)
        return
      }
      setMenuStyle(computeMenuStyle())
      const onScroll = (e: Event) => {
        if (menuRef.current?.contains(e.target as Node)) return
        close()
      }
      window.addEventListener('scroll', onScroll, true)
      window.addEventListener('resize', close)
      return () => {
        window.removeEventListener('scroll', onScroll, true)
        window.removeEventListener('resize', close)
      }
    }, [open, computeMenuStyle, close])

    // Close on outside click — checks both trigger and menu
    useEffect(() => {
      if (!open) return
      const handler = (e: MouseEvent) => {
        const target = e.target as Node
        if (triggerRef.current?.contains(target)) return
        if (menuRef.current?.contains(target)) return
        close()
      }
      window.addEventListener('mousedown', handler)
      return () => window.removeEventListener('mousedown', handler)
    }, [open, close])

    // Keyboard nav while the menu is open. The trigger keeps DOM focus the whole
    // time (options are inert), so every branch that shadows a native <button>
    // behaviour must preventDefault or the browser will also activate the trigger.
    useEffect(() => {
      if (!open) return
      const handler = (e: KeyboardEvent) => {
        const lastIndex = flatOptions.length - 1
        if (e.key === 'Escape') {
          e.preventDefault()
          close()
          return
        }
        // Tab is NOT trapped — the user asked to leave, so focus moves on
        // naturally (no preventDefault). But the menu must go with it: it is
        // portalled and viewport-fixed, so leaving it up would strand a floating
        // panel at the old trigger position, with a stale `aria-activedescendant`
        // pointing into it from a trigger that no longer has focus.
        if (e.key === 'Tab') {
          close()
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setActiveIndex((i) => Math.min(lastIndex, (i < 0 ? -1 : i) + 1))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setActiveIndex((i) => Math.max(0, (i < 0 ? flatOptions.length : i) - 1))
          return
        }
        // Home / End — cheap, and the grouped lists this component is used for are
        // long enough that arrow-keying to either end is not a real option.
        if (e.key === 'Home') {
          e.preventDefault()
          if (lastIndex >= 0) setActiveIndex(0)
          return
        }
        if (e.key === 'End') {
          e.preventDefault()
          if (lastIndex >= 0) setActiveIndex(lastIndex)
          return
        }
        // Enter AND Space both commit the active option (ARIA select-only
        // combobox). Space MUST preventDefault: the focused element is still the
        // trigger <button>, so native activation would fire its onClick and
        // dismiss the menu without ever selecting anything.
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Space' || e.key === 'Spacebar') {
          e.preventDefault()
          const opt = activeIndex >= 0 ? flatOptions[activeIndex] : undefined
          if (opt) onChange(opt.value)
          // Empty list: nothing to commit, but don't leave the menu hanging.
          close()
          return
        }
        // Printable-character typeahead. Never hijack modified keys — Ctrl/Meta/Alt
        // combinations belong to the browser and the OS.
        if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return
        e.preventDefault()
        const ta = typeaheadRef.current
        if (ta.timer !== null) window.clearTimeout(ta.timer)
        ta.buffer += e.key.toLowerCase()
        ta.timer = window.setTimeout(resetTypeahead, TYPEAHEAD_RESET_MS)
        // A lone character advances to the NEXT match so repeating it cycles
        // through same-initial options; a grown prefix re-matches from the current
        // row so a still-matching row stays put. Wraps in both cases. Options with
        // a non-string label are skipped by `typeaheadLabel`.
        // (Space commits, above — so a multi-word prefix isn't expressible.)
        const from = ta.buffer.length === 1 ? activeIndex + 1 : Math.max(activeIndex, 0)
        for (let n = 0; n <= lastIndex; n++) {
          const idx = (from + n) % flatOptions.length
          const label = typeaheadLabel(flatOptions[idx])
          if (label && label.startsWith(ta.buffer)) {
            setActiveIndex(idx)
            break
          }
        }
      }
      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }, [open, activeIndex, flatOptions, onChange, close, resetTypeahead])

    // Keep the active option inside the menu's own scroller — `menuMaxHeight`
    // clamps the list, so arrow-keying past the visible window would otherwise
    // move the highlight (and `aria-activedescendant`) somewhere unseen.
    //
    // `block: 'nearest'` is a no-op when the option is already visible, so this
    // never jars the page. Safe against §54: the scroll-dismiss handler above
    // early-returns when the scroll event target is inside `menuRef`, and the
    // element scrolled here is a descendant of that node, so a programmatic
    // scroll of the menu cannot self-dismiss it.
    //
    // `menuStyle` is a dependency, not just a guard: the `<ul>` only mounts on
    // the commit that sets it, so without it the seeded option (e.g. a
    // selection far down a long list) would never be scrolled to on open.
    useEffect(() => {
      if (!open || !menuStyle || activeIndex < 0) return
      const el = document.getElementById(optionId(activeIndex))
      if (el && menuRef.current?.contains(el)) el.scrollIntoView({ block: 'nearest' })
    }, [open, menuStyle, activeIndex, optionId])

    // Renders a single interactive option. `flatIndex` is its position in
    // `flatOptions` so active-highlight + keyboard nav stay in sync across groups.
    //
    // One node, one role (§57): the `<li role="option">` IS the interactive
    // element — it carries the handlers and the hover/active styling, and its
    // children are inert `<span>`s. Nesting a focusable <button> inside an
    // option is invalid ARIA. Options are deliberately not focusable; keyboard
    // nav lives on the window listener above and is surfaced via
    // `aria-activedescendant` on the trigger.
    //
    // Both the React key and the DOM id come from `flatIndex`, never from
    // `opt.value`: values are not guaranteed unique (the same option is often
    // offered under two groups), and keying on one produced a duplicate-key
    // warning plus an id collision that pointed `aria-activedescendant` and the
    // scroll-into-view lookup at the first row sharing that value instead of the
    // row the user was actually on. See `selectedIndex` for the matching contract.
    const renderOption = (opt: SelectOption, flatIndex: number) => {
      const isSelected = flatIndex === selectedIndex
      const isActive = activeIndex === flatIndex
      return (
        <li
          key={flatIndex}
          id={optionId(flatIndex)}
          role="option"
          aria-selected={isSelected}
          onClick={() => {
            onChange(opt.value)
            close()
          }}
          onMouseEnter={() => setActiveIndex(flatIndex)}
          className={cn(
            'flex w-full cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5 rounded transition-colors duration-100',
            'text-foreground dark:text-[#f7f8f8]',
            isActive
              ? 'bg-accent/60 dark:bg-white/[0.06]'
              : 'hover:bg-accent/40 dark:hover:bg-white/[0.04]',
            isSelected && 'text-[#5e6ad2] dark:text-[#a5b4fc]',
          )}
        >
          <span className="flex flex-col min-w-0 text-left">
            <span className="truncate" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
              {opt.label}
            </span>
            {opt.description && (
              <span
                className="truncate font-normal text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '11px', letterSpacing: '-0.06px', marginTop: '1px' }}
              >
                {opt.description}
              </span>
            )}
          </span>
          {isSelected && <Check className="h-3.5 w-3.5 flex-shrink-0" weight="bold" />}
        </li>
      )
    }

    return (
      <div
        className={cn('relative inline-block', className)}
        style={minWidth ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth } : undefined}
      >
        <button
          ref={setTriggerRef}
          id={id}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid || undefined}
          disabled={disabled}
          onClick={() => {
            if (disabled) return
            if (open) close()
            else openMenu('selected')
          }}
          // A closed trigger must open on ArrowDown / ArrowUp / Enter / Space
          // (ARIA select-only combobox). This lives on the trigger rather than
          // the window listener below, which only mounts while the menu is
          // already open. Bail while open so the two never both handle a key.
          onKeyDown={(e) => {
            if (disabled || open) return
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              openMenu('first')
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              openMenu('last')
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openMenu('selected')
            }
          }}
          // Belt-and-braces for the Tab case above, and the only cover for focus
          // moved programmatically (a modal autofocusing, a router transition):
          // a viewport-fixed portalled menu must never outlive the focus that owns
          // it. Cannot fight the outside-mousedown handler — `close()` is
          // idempotent, so the worst case is a no-op second call.
          onBlur={(e) => {
            if (!open) return
            const next = e.relatedTarget as Node | null
            // A null relatedTarget means focus went nowhere focusable — which is
            // exactly what mousedown on an option does, since options are inert
            // <li>s. Closing here would unmount the menu between mousedown and
            // click and swallow the selection, so leave that path to the
            // outside-mousedown handler, which already knows to ignore the menu.
            if (!next) return
            if (triggerRef.current?.contains(next)) return
            if (menuRef.current?.contains(next)) return
            close()
          }}
          className={cn(
            'group inline-flex items-center justify-between gap-2 h-9 w-full rounded-md px-3 transition-colors duration-150',
            'bg-background border border-border/60 text-foreground',
            'hover:border-border/80 hover:bg-background',
            'focus:outline-none focus-visible:border-[#7170ff]/55',
            'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8]',
            'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
            'dark:focus-visible:border-[#7170ff]/75',
            'disabled:opacity-60 disabled:cursor-not-allowed',
            open && 'border-[#7170ff]/55 dark:border-[#7170ff]/75',
            buttonClassName,
          )}
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          <span className="inline-flex items-center gap-1.5 min-w-0">
            {icon && (
              <span className="flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" aria-hidden="true">
                {icon}
              </span>
            )}
            <span className={cn('truncate', !selected && 'text-muted-foreground dark:text-[#62666d]')}>
              {selected ? selected.label : placeholder}
            </span>
          </span>
          <CaretDown
            className={cn(
              'h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-150',
              open && 'rotate-180',
            )}
            weight="bold"
          />
        </button>

        {createPortal(
          <AnimatePresence>
            {open && menuStyle && (
              <motion.div
                initial={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                style={menuStyle}
                className={menuStyle.bottom != null ? 'origin-bottom' : 'origin-top'}
              >
                <ul
                  ref={menuRef}
                  id={listboxId}
                  role="listbox"
                  tabIndex={-1}
                  style={{ maxHeight: menuStyle.maxHeight }}
                  className={cn(
                    'overflow-y-auto rounded-lg p-1',
                    'bg-card border border-border/60 shadow-lg',
                    'dark:bg-[#191a1b] dark:border-white/[0.08]',
                    'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
                  )}
                >
                  {groups
                    ? groups.map((group, gi) => {
                        const start = groups
                          .slice(0, gi)
                          .reduce((n, g) => n + g.options.length, 0)
                        // Keyed on position, not label — two groups may share a
                        // label just as two options may share a value.
                        return (
                          <React.Fragment key={`grp-${gi}`}>
                            <li
                              role="presentation"
                              className="px-2.5 pt-2.5 pb-1 first:pt-1 uppercase text-muted-foreground/70 dark:text-[#62666d] select-none"
                              style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.09em' }}
                            >
                              {group.label}
                            </li>
                            {group.options.map((opt, oi) => renderOption(opt, start + oi))}
                          </React.Fragment>
                        )
                      })
                    : flatOptions.map((opt, i) => renderOption(opt, i))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
      </div>
    )
  },
)
Select.displayName = 'Select'

export { Select }
